import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import {
  animationFrameScheduler,
  filter,
  finalize,
  fromEvent,
  map,
  observeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Vector3 } from 'three';

import { useSubscriptions } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useEditingLabelBoxStore, useToolMainCameraStore } from '@/store';
import { type NEW__LabelingBox, withCanvasXY, withTarget } from '@/util';

import {
  applyAngleRadToBox,
  getAngleRadBetweenTwoPoints,
  isEnabled,
  isSelectedLabelingBoxList,
  isTopMode,
  setControlEnabled,
  validMouseEventAndCanvasElement,
  withIntersectPlanePoint,
} from '../util';

const useDefaultViewTopRotate = ({
  setRenderBoxListById,
  disabled,
}: {
  setRenderBoxListById: (boxIdList: string[]) => void;
  disabled: boolean;
}) => {
  const { getMode } = useToolMainCameraStore();
  const { camera, controls } = useThree();
  const { selectedLabelingBoxList, updateQuaternion } = useLabelingBoxContext();
  const { setRotationZ, clear } = useEditingLabelBoxStore();
  const selectedLabelingBoxListRef = useRef<NEW__LabelingBox[]>([]);

  const prepareRotate = () => {
    setControlEnabled(false, controls);
    setRenderBoxListById(selectedLabelingBoxListRef.current.map(v => v.id));
  };

  const cleanupRotate = () => {
    clear();
    setControlEnabled(true, controls);
    setRenderBoxListById([]);
  };

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousedown')
        .pipe(
          filter(() => isTopMode(getMode())),
          filter(() => isEnabled(disabled)),
          filter(e => validMouseEventAndCanvasElement(e, 2)),
          filter(() =>
            isSelectedLabelingBoxList(selectedLabelingBoxListRef.current),
          ),
          withTarget,
          withCanvasXY,
          tap(prepareRotate),
          map(e => {
            const mouseDownPosition = withIntersectPlanePoint({
              event: e,
              camera,
              coplanarPoint: new Vector3(0, 0, 0),
            });

            return { event: e, mouseDownPosition };
          }),
          switchMap(({ mouseDownPosition }) => {
            const firstSelectedBoxPosition =
              selectedLabelingBoxListRef.current[0].boxMesh.position;
            const mouseDownAngleRad = getAngleRadBetweenTwoPoints(
              mouseDownPosition,
              firstSelectedBoxPosition,
            );

            return fromEvent<MouseEvent>(window, 'mousemove').pipe(
              withTarget,
              withCanvasXY,
              tap(mouseMoveEvent => {
                const mouseMovePosition = withIntersectPlanePoint({
                  event: mouseMoveEvent,
                  camera,
                  coplanarPoint: new Vector3(0, 0, 0),
                });
                const mouseMoveAngleRad = getAngleRadBetweenTwoPoints(
                  mouseMovePosition,
                  firstSelectedBoxPosition,
                );
                const angleRad = mouseMoveAngleRad - mouseDownAngleRad;
                setRotationZ(angleRad);
              }),
              takeUntil(
                fromEvent<MouseEvent>(window, 'mouseup').pipe(
                  withTarget,
                  withCanvasXY,
                  tap(mouseUpEvent => {
                    const mouseUpPosition = withIntersectPlanePoint({
                      event: mouseUpEvent,
                      camera,
                      coplanarPoint: new Vector3(0, 0, 0),
                    });
                    const mouseUpAngleRad = getAngleRadBetweenTwoPoints(
                      mouseUpPosition,
                      firstSelectedBoxPosition,
                    );

                    updateQuaternion(
                      selectedLabelingBoxListRef.current.map(
                        applyAngleRadToBox(
                          mouseUpAngleRad - mouseDownAngleRad,
                          'z',
                        ),
                      ),
                    );
                  }),
                  observeOn(animationFrameScheduler),
                  finalize(cleanupRotate),
                ),
              ),
              finalize(cleanupRotate),
            );
          }),
          finalize(cleanupRotate),
        )
        .subscribe(),
    ],
    [camera, controls, disabled],
  );

  useEffect(() => {
    selectedLabelingBoxListRef.current = selectedLabelingBoxList;
  }, [selectedLabelingBoxList]);

  return {};
};

export default useDefaultViewTopRotate;
